<script setup>
import { ref, getCurrentInstance, computed } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    client: Object,
    consultants: Array,
    sessions: Array,
    exchangeData: Object,
});

// Tab management
const activeTab = ref(0);
const tabs = [
    { label: $t('Account'), icon: 'pi pi-user' },
    { label: $t('Orders'), icon: 'pi pi-shopping-cart' },
    { label: $t('Invoices'), icon: 'pi pi-dollar' },
    { label: $t('Logins'), icon: 'pi pi-lock' },
    { label: $t('Views'), icon: 'pi pi-eye' },
    { label: $t('Misc'), icon: 'pi pi-cog' },
];

// Form and photo management
const verificationLinkSent = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);
const passwordInput = ref(null);
const showPassword = ref(false);
const deleteClientDialog = ref(false);

// Initialize form
const form = useForm({
    _method: 'PUT',

    seller_id: props.client?.seller_id || null,

    // Personal info
    first_name: props.client?.first_name || '',
    last_name: props.client?.last_name || '',
    name: props.client?.name || '',
    birthday: props.client?.birthday || '',
    email: props.client?.email || '',
    photo: null,

    // Settings
    locale: props.client?.locale || 'en',
    coin: props.client?.coin || 'USD',
    full: props.client?.full || false,

    // Company info
    company_name: props.client?.company_name || '',
    website_url: props.client?.website_url || '',
    address_country: props.client?.address_country || '',
    address_state: props.client?.address_state || '',
    address_street_name: props.client?.address_street_name || '',
    address_street_number: props.client?.address_street_number || '',
    address_number: props.client?.address_number || '',
    address_zipcode: props.client?.address_zipcode || '',

    // Contact info
    phone: props.client?.phone || '',
    notify_updates_via_email: props.client?.notify_updates_via_email || null,

    contact: props.client?.contact || '',
    contact_where: props.client?.contact_where || '',
    contact_id: props.client?.contact_id || '',
    contact_how: props.client?.contact_how || '',

    allow_deferred_payment: props.client?.allow_deferred_payment || false,

    // Invoice info
    invoice_company_name: props.client?.invoice_company_name || '',
    invoice_vat_number: props.client?.invoice_vat_number || '',
    invoice_paypal: props.client?.invoice_paypal || '',
    invoice_address_country: props.client?.invoice_address_country || '',
    invoice_address_state: props.client?.invoice_address_state || '',
    invoice_address_street_name: props.client?.invoice_address_street_name || '',
    invoice_address_street_number: props.client?.invoice_address_street_number || '',
    invoice_address_number: props.client?.invoice_address_number || '',
    invoice_address_zipcode: props.client?.invoice_address_zipcode || '',

    password: '',
});

// Options for selects
const localeOptions = computed(() => [
    { label: $t('English'), value: 'en' },
    { label: $t('Español'), value: 'es' },
    { label: $t('Português'), value: 'pt' },
]);

const coinOptions = computed(() => [
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'BRL', value: 'BRL' },
    { label: 'GBP', value: 'GBP' },
]);

const dynamicCurrencyOptions = computed(() => {
    if (!props.exchangeData) {
        return [
            { label: 'BRL', value: 'BRL' },
            { label: 'EUR', value: 'EUR' },
            { label: 'USD', value: 'USD' },
            { label: 'GBP', value: 'GBP' },
        ];
    }
    
    return props.exchangeData.map(exchange => ({
        label: exchange.currency,
        value: exchange.currency
    }));
});

const notificationOptions = computed( () => [
    { label: $t('Disabled'), value: 'DISABLED' },
    { label: $t('Minimal'), value: 'MINIMAL' },
    { label: $t('All'), value: 'ALL' },
]);

// Functions
const update = () => {
    if (photoInput.value?.files[0]) {
        form.photo = photoInput.value.files[0];
    }

    form.post(route('clients.update', props.client.id), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onStart: () => showLoading('Updating client...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            clearPhotoFileInput();
            form.reset('password');
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Client updated successfully'),
                life: 3000
            });
        },
        onError: (error) => {
            console.log(error);
            if (form.errors.password) {
                form.reset('password');
                passwordInput.value?.focus();
            }
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update client'),
                life: 3000
            });
        },
    });
};



const sendEmailVerification = () => {
    verificationLinkSent.value = true;
};

const selectNewPhoto = () => {
    photoInput.value?.click();
};

const updatePhotoPreview = () => {
    const photo = photoInput.value?.files[0];

    if (!photo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo);
};

const deletePhoto = () => {
    // Implement photo deletion if needed
    photoPreview.value = null;
    clearPhotoFileInput();
};

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null;
    }
};

const generatePassword = () => {
    form.password = Math.random().toString(36).slice(-8);
};

const confirmDeleteClient = () => {
    deleteClientDialog.value = true;
};

const deleteClient = () => {
    router.delete(route('clients.destroy', props.client.id), {
        onStart: () => showLoading('Deleting client...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Client deleted successfully'),
                life: 3000
            });
            router.visit(route('clients.index'), {
                onStart: () => showLoading('Loading...'),
                onFinish: () => hideLoading()
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to delete client'),
                life: 3000
            });
        }
    });
};

const formatDate = (dateString) => {
    if (!dateString) return $t('Never');
    return new Date(dateString).toLocaleString();
};

const goBack = () => {
    router.visit(route('clients.index'), {
        onStart: () => showLoading('Loading...'),
        onFinish: () => hideLoading()
    });
};

defineOptions({
    layout: AdminMainLayout,
});
</script>

<template>
    <div>
        <!-- Header with back button -->
        <div class="mb-6">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <Button
                        icon="pi pi-arrow-left"
                        severity="secondary"
                        text
                        rounded
                        @click="goBack"
                    />
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">{{ $t('Edit Client') }}</h1>
                        <p class="text-surface-600">{{ client?.name || client?.email }}</p>
                    </div>
                </div>
                <div class="flex gap-2">
                    <Button
                        :label="$t('Delete Client')"
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        @click="confirmDeleteClient"
                    />
                </div>
            </div>
        </div>

        <!-- Consultant Selection Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex items-center justify-between">
                    <div>
                        <label class="block text-sm font-medium mb-2">{{ $t('Current Linking consultant') }}</label>
                        <div class="flex items-center gap-2">
                            <Select
                                v-model="form.seller_id"
                                :options="consultants"
                                optionLabel="name"
                                optionValue="id"
                                :placeholder="$t('None')"
                                class="w-64"
                                showClear
                            />
                            <Button
                                :label="$t('Save')"
                                icon="pi pi-save"
                                :loading="form.processing"
                                @click="update"
                            />
                        </div>
                    </div>
                    <div class="text-sm text-surface-500">
                        {{ $t('Last seen') }}: {{ formatDate(client?.latest_login?.created_at) }}
                    </div>
                </div>
            </template>
        </Card>

        <!-- Main Content with Tabs -->
        <Card>
            <template #content>
                <TabView v-model:active="activeTab">
                    <TabPanel v-for="(tab, index) in tabs" :key="index">
                        <template #header>
                            <div class="flex items-center gap-2">
                                <i :class="tab.icon"></i>
                                <span>{{ tab.label }}</span>
                            </div>
                        </template>

                        <!-- Account Tab -->
                        <div v-if="index === 0" class="space-y-6">
                            <!-- Personal Information Section -->
                            <div class="border rounded-lg p-6">
                                <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <i class="pi pi-user"></i>
                                    {{ $t('Personal Information') }}
                                </h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('First name') }} <span class="text-red-500">*</span></label>
                                        <InputText v-model="form.first_name" class="w-full" :class="{ 'p-invalid': form.errors.first_name }"/>
                                        <small v-if="form.errors.first_name" class="p-error">{{ form.errors.first_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Last name') }} <span class="text-red-500">*</span></label>
                                        <InputText v-model="form.last_name" class="w-full" :class="{ 'p-invalid': form.errors.last_name }"/>
                                        <small v-if="form.errors.last_name" class="p-error">{{ form.errors.last_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Birthday') }}</label>
                                        <Calendar v-model="form.birthday" dateFormat="yy-mm-dd" class="w-full" showIcon />
                                        <small v-if="form.errors.birthday" class="p-error">{{ form.errors.birthday }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Locale') }}</label>
                                        <Select
                                            v-model="form.locale"
                                            :options="localeOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                        <small v-if="form.errors.locale" class="p-error">{{ form.errors.locale }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Currency') }}</label>
                                        <Select
                                            v-model="form.coin"
                                            :options="dynamicCurrencyOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                        />
                                        <small v-if="form.errors.coin" class="p-error">{{ form.errors.coin }}</small>
                                    </div>

                                    <div class="md:col-span-2 flex items-center gap-2">
                                        <Checkbox v-model="form.full" :binary="true" inputId="full" />
                                        <label for="full" class="font-medium">{{ $t('Full Client') }}</label>
                                        <Tag v-if="form.full" value="FULL" severity="warn" icon="pi pi-star-fill" />
                                    </div>

                                    <div class="md:col-span-2 flex items-center gap-2">
                                        <Checkbox v-model="form.allow_deferred_payment" :binary="true" inputId="allow_deferred_payment" />
                                        <label for="allow_deferred_payment" class="font-medium">{{ $t('Allow Deferred Payment') }}</label>
                                        <Tag v-if="form.allow_deferred_payment" value="ENABLED" severity="info" icon="pi pi-calendar" />
                                    </div>

                                    <div class="md:col-span-2">
                                        <Button
                                            :label="$t('Save')"
                                            icon="pi pi-save"
                                            :loading="form.processing"
                                            :disabled="form.processing"
                                            @click="update"
                                        />
                                        <span v-if="form.recentlySuccessful" class="ml-3 text-green-600">
                                            <i class="pi pi-check-circle"></i> {{ $t('Saved') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Company Information Section -->
                            <div class="border rounded-lg p-6 bg-surface-50">
                                <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <i class="pi pi-building"></i>
                                    {{ $t('Company Information') }}
                                </h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Company name') }}</label>
                                        <InputText v-model="form.company_name" class="w-full" />
                                        <small v-if="form.errors.company_name" class="p-error">{{ form.errors.company_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Website URL') }}</label>
                                        <InputText v-model="form.website_url" class="w-full" />
                                        <small v-if="form.errors.website_url" class="p-error">{{ form.errors.website_url }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Country') }}</label>
                                        <InputText v-model="form.address_country" class="w-full" />
                                        <small v-if="form.errors.address_country" class="p-error">{{ form.errors.address_country }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('State') }}</label>
                                        <InputText v-model="form.address_state" class="w-full" />
                                        <small v-if="form.errors.address_state" class="p-error">{{ form.errors.address_state }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Street name') }}</label>
                                        <InputText v-model="form.address_street_name" class="w-full" />
                                        <small v-if="form.errors.address_street_name" class="p-error">{{ form.errors.address_street_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Number') }}</label>
                                        <InputText v-model="form.address_street_number" class="w-full" />
                                        <small v-if="form.errors.address_street_number" class="p-error">{{ form.errors.address_street_number }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Apartment, suite, etc') }}</label>
                                        <InputText v-model="form.address_number" class="w-full" />
                                        <small v-if="form.errors.address_number" class="p-error">{{ form.errors.address_number }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Zip code') }}</label>
                                        <InputText v-model="form.address_zipcode" class="w-full" />
                                        <small v-if="form.errors.address_zipcode" class="p-error">{{ form.errors.address_zipcode }}</small>
                                    </div>

                                    <div class="md:col-span-2">
                                        <Button
                                            :label="$t('Save')"
                                            icon="pi pi-save"
                                            :loading="form.processing"
                                            @click="update"
                                        />
                                        <span v-if="form.recentlySuccessful" class="ml-3 text-green-600">
                                            <i class="pi pi-check-circle"></i> {{ $t('Saved') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Contact Information Section -->
                            <div class="border rounded-lg p-6">
                                <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <i class="pi pi-phone"></i>
                                    {{ $t('Contact Information') }}
                                </h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Email') }} <span class="text-red-500">*</span></label>
                                        <InputText v-model="form.email" type="email" class="w-full" />
                                        <small v-if="form.errors.email" class="p-error">{{ form.errors.email }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Phone') }}</label>
                                        <InputText v-model="form.phone" class="w-full" />
                                        <small v-if="form.errors.phone" class="p-error">{{ form.errors.phone }}</small>
                                    </div>

                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium mb-2">{{ $t('Email notifications') }}</label>
                                        <Select
                                            v-model="form.notify_updates_via_email"
                                            :options="notificationOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            class="w-full"
                                            :placeholder="$t('Select notification frequency')"
                                        />
                                        <small v-if="form.errors.notify_updates_via_email" class="p-error">{{ form.errors.notify_updates_via_email }}</small>
                                    </div>

                                    <div class="md:col-span-2">
                                        <Button
                                            :label="$t('Save')"
                                            icon="pi pi-save"
                                            :loading="form.processing"
                                            @click="update"
                                        />
                                        <span v-if="form.recentlySuccessful" class="ml-3 text-green-600">
                                            <i class="pi pi-check-circle"></i> {{ $t('Saved') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Invoice Information Section -->
                            <div class="border rounded-lg p-6 bg-surface-50">
                                <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <i class="pi pi-dollar"></i>
                                    {{ $t('Invoice Information') }}
                                </h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Company name') }}</label>
                                        <InputText v-model="form.invoice_company_name" class="w-full" />
                                        <small v-if="form.errors.invoice_company_name" class="p-error">{{ form.errors.invoice_company_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('VAT number') }}</label>
                                        <InputText v-model="form.invoice_vat_number" class="w-full" />
                                        <small v-if="form.errors.invoice_vat_number" class="p-error">{{ form.errors.invoice_vat_number }}</small>
                                    </div>

                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium mb-2">{{ $t('PayPal') }}</label>
                                        <InputText v-model="form.invoice_paypal" type="email" class="w-full" placeholder="paypal@example.com" />
                                        <small v-if="form.errors.invoice_paypal" class="p-error">{{ form.errors.invoice_paypal }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Country') }}</label>
                                        <InputText v-model="form.invoice_address_country" class="w-full" />
                                        <small v-if="form.errors.invoice_address_country" class="p-error">{{ form.errors.invoice_address_country }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('State') }}</label>
                                        <InputText v-model="form.invoice_address_state" class="w-full" />
                                        <small v-if="form.errors.invoice_address_state" class="p-error">{{ form.errors.invoice_address_state }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Street name') }}</label>
                                        <InputText v-model="form.invoice_address_street_name" class="w-full" />
                                        <small v-if="form.errors.invoice_address_street_name" class="p-error">{{ form.errors.invoice_address_street_name }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Number') }}</label>
                                        <InputText v-model="form.invoice_address_street_number" class="w-full" />
                                        <small v-if="form.errors.invoice_address_street_number" class="p-error">{{ form.errors.invoice_address_street_number }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Apartment, suite, etc') }}</label>
                                        <InputText v-model="form.invoice_address_number" class="w-full" />
                                        <small v-if="form.errors.invoice_address_number" class="p-error">{{ form.errors.invoice_address_number }}</small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Zip code') }}</label>
                                        <InputText v-model="form.invoice_address_zipcode" class="w-full" />
                                        <small v-if="form.errors.invoice_address_zipcode" class="p-error">{{ form.errors.invoice_address_zipcode }}</small>
                                    </div>

                                    <div class="md:col-span-2">
                                        <Button
                                            :label="$t('Save')"
                                            icon="pi pi-save"
                                            :loading="form.processing"
                                            @click="update"
                                        />
                                        <span v-if="form.recentlySuccessful" class="ml-3 text-green-600">
                                            <i class="pi pi-check-circle"></i> {{ $t('Saved') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Password Section -->
                            <div class="border rounded-lg p-6">
                                <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <i class="pi pi-key"></i>
                                    {{ $t('Change password') }}
                                </h3>
                                <p class="text-sm text-surface-500 mb-4">{{ $t('The password must be at least 8 characters long') }}</p>

                                <div class="space-y-4">
                                    <div>
                                        <label class="block text-sm font-medium mb-2">{{ $t('Password') }}</label>
                                        <div class="flex gap-2">
                                            <Password
                                                v-model="form.password"
                                                ref="passwordInput"
                                                :toggleMask="true"
                                                class="w-full"
                                                :feedback="false"
                                            />
                                            <Button
                                                :label="$t('Generate')"
                                                icon="pi pi-refresh"
                                                severity="secondary"
                                                @click="generatePassword"
                                            />
                                        </div>
                                        <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                                    </div>

                                    <div>
                                        <Button
                                            :label="$t('Save')"
                                            icon="pi pi-save"
                                            :loading="form.processing"
                                            @click="update"
                                        />
                                        <span v-if="form.recentlySuccessful" class="ml-3 text-green-600">
                                            <i class="pi pi-check-circle"></i> {{ $t('Saved') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Orders Tab -->
                        <div v-if="index === 1">
                            <DataTable
                                :value="client?.orders || []"
                                :paginator="true"
                                :rows="10"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                :currentPageReportTemplate="$t('Showing {first} to {last} of {totalRecords} orders')"
                            >
                                <template #empty>
                                    <div class="text-center py-8">
                                        <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                                        <p class="text-surface-500">{{ $t('No orders found') }}</p>
                                    </div>
                                </template>

                                <Column field="number" :header="$t('Number')" sortable></Column>
                                <Column field="created_at" :header="$t('Date')" sortable>
                                    <template #body="slotProps">
                                        {{ formatDate(slotProps.data.created_at) }}
                                    </template>
                                </Column>
                                <Column :header="$t('Actions')">
                                    <template #body="slotProps">
                                        <Button
                                            icon="pi pi-eye"
                                            severity="info"
                                            text
                                            rounded
                                            v-tooltip.top="$t('View')"
                                            @click="router.visit(route('orders.show', slotProps.data.id), {
                                                onStart: () => showLoading('Loading...'),
                                                onFinish: () => hideLoading()
                                            })"
                                        />
                                    </template>
                                </Column>
                            </DataTable>
                        </div>

                        <!-- Invoices Tab -->
                        <div v-if="index === 2">
                            <DataTable
                                :value="client?.invoices || []"
                                :paginator="true"
                                :rows="10"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                :currentPageReportTemplate="$t('Showing {first} to {last} of {totalRecords} invoices')"
                            >
                                <template #empty>
                                    <div class="text-center py-8">
                                        <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                                        <p class="text-surface-500">{{ $t('No invoices found') }}</p>
                                    </div>
                                </template>

                                <Column field="number" :header="$t('Number')" sortable>
                                    <template #body="slotProps">
                                        <a
                                            :href="route('invoices.edit', slotProps.data.id)"
                                            target="_blank"
                                            class="flex items-center gap-1 text-primary-500 hover:text-primary-700"
                                        >
                                            <span>#{{ slotProps.data.number }}</span>
                                            <i class="pi pi-external-link text-xs"></i>
                                        </a>
                                    </template>
                                </Column>
                                <Column field="created_at" :header="$t('Date')" sortable>
                                    <template #body="slotProps">
                                        {{ formatDate(slotProps.data.created_at) }}
                                    </template>
                                </Column>
                            </DataTable>
                        </div>

                        <!-- Logins Tab -->
                        <div v-if="index === 3">
                            <DataTable
                                :value="client?.logins || []"
                                :paginator="true"
                                :rows="10"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                :currentPageReportTemplate="$t('Showing {first} to {last} of {totalRecords} logins')"
                            >
                                <template #empty>
                                    <div class="text-center py-8">
                                        <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                                        <p class="text-surface-500">{{ $t('No logins found') }}</p>
                                    </div>
                                </template>

                                <Column field="created_at" :header="$t('Date')" sortable>
                                    <template #body="slotProps">
                                        {{ formatDate(slotProps.data.created_at) }}
                                    </template>
                                </Column>
                            </DataTable>
                        </div>

                        <!-- Views Tab -->
                        <div v-if="index === 4">
                            <DataTable
                                :value="client?.views || []"
                                :paginator="true"
                                :rows="10"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                :currentPageReportTemplate="$t('Showing {first} to {last} of {totalRecords} views')"
                            >
                                <template #empty>
                                    <div class="text-center py-8">
                                        <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                                        <p class="text-surface-500">{{ $t('No views found') }}</p>
                                    </div>
                                </template>

                                <Column field="site.url" :header="$t('Site')" sortable></Column>
                                <Column field="created_at" :header="$t('Date')" sortable>
                                    <template #body="slotProps">
                                        {{ formatDate(slotProps.data.created_at) }}
                                    </template>
                                </Column>
                            </DataTable>
                        </div>

                        <!-- Misc Tab -->
                        <div v-if="index === 5" class="space-y-6">
                            <!-- Projects Section -->
                            <div>
                                <h3 class="text-lg font-semibold mb-4">{{ $t('Projects') }}</h3>
                                <DataTable
                                    :value="client?.projects || []"
                                    :paginator="false"
                                >
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <p class="text-surface-500 italic">{{ $t('No projects have been added by this client yet.') }}</p>
                                        </div>
                                    </template>

                                    <Column field="name" :header="$t('Project Name')">
                                        <template #body="slotProps">
                                            <div class="flex items-center gap-2">
                                                <span
                                                    class="w-3 h-3 rounded-full"
                                                    :style="{ backgroundColor: slotProps.data.color || '#666' }"
                                                ></span>
                                                <span>{{ slotProps.data.name }}</span>
                                            </div>
                                        </template>
                                    </Column>
                                    <Column field="sites_count" :header="$t('Sites Count')" style="width: 150px"></Column>
                                </DataTable>
                            </div>

                            <!-- Favorites Section -->
                            <div>
                                <h3 class="text-lg font-semibold mb-4">{{ $t('Favorites') }}</h3>
                                <DataTable
                                    :value="client?.favorites || []"
                                    :paginator="false"
                                >
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <p class="text-surface-500 italic">{{ $t('No favorites have been added by this client yet.') }}</p>
                                        </div>
                                    </template>

                                    <Column field="url" :header="$t('Site URL')"></Column>
                                </DataTable>
                            </div>

                            <!-- Interests Section -->
                            <div>
                                <h3 class="text-lg font-semibold mb-4">{{ $t('Interests') }}</h3>
                                <DataTable
                                    :value="client?.interests || []"
                                    :paginator="false"
                                >
                                    <template #empty>
                                        <div class="text-center py-4">
                                            <p class="text-surface-500 italic">{{ $t('No interests have been added by this client yet.') }}</p>
                                        </div>
                                    </template>

                                    <Column field="url" :header="$t('Site URL')"></Column>
                                </DataTable>
                            </div>

                            <!-- Browser Sessions Section -->
                            <div v-if="sessions?.length > 0">
                                <h3 class="text-lg font-semibold mb-4">{{ $t('Browser Sessions') }}</h3>
                                <DataTable
                                    :value="sessions"
                                    :paginator="false"
                                >
                                    <Column field="agent.browser" :header="$t('Browser')"></Column>
                                    <Column field="agent.platform" :header="$t('Platform')"></Column>
                                    <Column field="ip_address" :header="$t('IP Address')"></Column>
                                    <Column field="last_active" :header="$t('Last Active')">
                                        <template #body="slotProps">
                                            <Tag
                                                v-if="slotProps.data.is_current_device"
                                                :value="$t('Current Device')"
                                                severity="success"
                                            />
                                            <span v-else>{{ slotProps.data.last_active }}</span>
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>
                        </div>
                    </TabPanel>
                </TabView>
            </template>
        </Card>

        <!-- Delete Client Dialog -->
        <Dialog
            v-model:visible="deleteClientDialog"
            :style="{ width: '450px' }"
            :header="$t('Confirm')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span>
                    {{ $t('Are you sure you want to delete') }} <b>{{ client?.name || client?.email }}</b>?
                </span>
            </div>
            <template #footer>
                <Button
                    :label="$t('No')"
                    icon="pi pi-times"
                    text
                    @click="deleteClientDialog = false"
                />
                <Button
                    :label="$t('Yes')"
                    icon="pi pi-check"
                    severity="danger"
                    @click="deleteClient"
                />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
.p-error {
    @apply text-red-500 text-xs mt-1;
}

:deep(.p-select) {
    height: 42px;
}

:deep(.p-select .p-select-label) {
    padding: 8px 12px;
    line-height: 1.5;
}
</style>
